import React from "react";

const money = (cents) =>
  "$" + ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The single seller breakdown, rendered identically before listing, on the sold order, and on the
// payout: item/sale price, shipping (kept or absorbed), platform fee, processing (at cost), and what
// actually lands in the seller's pocket. YOU RECEIVE is the true take-home — anything that reduces
// it (including shipping the seller absorbs on a free-shipping listing) shows here and is deducted.
export function PayoutBreakdown({
  amountCents,
  shippingCents = 0,
  absorbedCents = 0,
  platformFeeCents,
  processingCents,
  payoutCents,
  ratePct,
  tierLabel,
  tierMismatch = false,
  entitledLabel,
  shippingUnknown = false,
  estimated = false,
  testId = "payout-breakdown",
}) {
  const hasShip = Number(shippingCents) > 0;
  const hasAbsorb = Number(absorbedCents) > 0;
  const rows = [
    { key: "price", label: (hasShip || hasAbsorb) ? "Item price" : "Sale price", value: money(amountCents), tone: "text-foreground", sign: "" },
  ];
  if (hasShip) rows.push({ key: "ship-keep", label: "Shipping (you keep)", value: money(shippingCents), tone: "text-foreground", sign: "+" });
  rows.push({ key: "fee", label: "Platform fee", value: money(platformFeeCents), tone: "text-[#C9A79C]", sign: "\u2212" });
  if (hasAbsorb) rows.push({ key: "absorb", label: "Shipping you absorb", value: money(absorbedCents), tone: "text-[#C9A79C]", sign: "\u2212" });

  return (
    <div className="border border-[#2E2F31] bg-[#111213]" data-testid={testId}>
      <div className="px-3.5 py-2.5 space-y-1.5">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between font-mono text-[12px]">
            <span className="uppercase tracking-wide text-[#8B8D90]">{r.label}</span>
            <span className={r.tone} data-testid={`${testId}-${r.key}`}>
              {r.sign}{r.value}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t border-[#2E2F31] px-3.5 py-2.5 flex items-center justify-between">
        <span className="font-mono uppercase tracking-wide text-[11px] text-[#9BB37A]">You receive</span>
        <span className="font-heading text-lg text-olive-bright" data-testid={`${testId}-you-receive`}>
          {money(payoutCents)}
        </span>
      </div>

      {/* Always-visible tier line — names the tier, not just the %, so a wrong rate is caught at a
          glance. Goes amber when the charged tier disagrees with an active membership (drift surfaced
          where the seller actually looks). */}
      {tierLabel && (
        <div className={`border-t border-[#2E2F31] px-3.5 py-1.5 ${tierMismatch ? "bg-[#3a2a12]" : ""}`}>
          {tierMismatch ? (
            <span className="font-mono text-[10px] uppercase tracking-wide text-[#E0A93B]" data-testid={`${testId}-tier-mismatch`}>
              Rate: {tierLabel} {ratePct != null ? `${ratePct}%` : ""} · your membership is {entitledLabel} — contact support, you're being charged the wrong rate
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-wide text-[#6C6F73]" data-testid={`${testId}-tier`}>
              Rate: {tierLabel} {ratePct != null ? `${ratePct}%` : ""}
            </span>
          )}
        </div>
      )}

      {/* Buyers cover payment processing on top of the sale price — it is NOT taken from the
          seller payout. Shown on every seller-facing breakdown so the take-home reads honestly. */}
      <div className="border-t border-[#2E2F31] px-3.5 py-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wide text-[#6C6F73]" data-testid={`${testId}-buyer-processing-note`}>
          Buyers cover payment processing at checkout — it's not taken from your payout
        </span>
      </div>

      {shippingUnknown && (
        <div className="border-t border-[#2E2F31] px-3.5 py-1.5">
          <span className="font-mono text-[10px] uppercase tracking-wide text-[#E0A93B]" data-testid={`${testId}-shipping-unknown`}>
            Estimate excludes shipping — enter your shipping cost to see your true take-home
          </span>
        </div>
      )}

      {estimated && (
        <div className="border-t border-[#2E2F31] px-3.5 py-1.5">
          <span className="font-mono text-[10px] uppercase tracking-wide text-[#6C6F73]" data-testid={`${testId}-estimate-note`}>
            Estimate · your platform fee only — buyers pay payment processing on top at checkout
          </span>
        </div>
      )}
    </div>
  );
}
